import type { Vaga } from "../../types/vaga";

interface VagaCardProps {
  vaga: Vaga;
}

function formatTimeAgo(date: Date): string {
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) {
    return "Hoje";
  } else if (diffInDays === 1) {
    return "Há 1 dia";
  } else if (diffInDays < 7) {
    return `Há ${diffInDays} dias`;
  } else if (diffInDays < 30) {
    const weeks = Math.floor(diffInDays / 7);
    return weeks === 1 ? "Há 1 semana" : `Há ${weeks} semanas`;
  } else {
    const months = Math.floor(diffInDays / 30);
    return months === 1 ? "Há 1 mês" : `Há ${months} meses`;
  }
}

export default function VagaCard({ vaga }: VagaCardProps) {
  const tipoLabel = vaga.tipoLabel ?? "Vaga aberta";
  const isVagaFutura = vaga.tipo === "futuras";
  const locationLabel =
    vaga.localizacao.estado &&
    vaga.localizacao.estado !== vaga.localizacao.cidade
      ? `${vaga.localizacao.cidade} - ${vaga.localizacao.estado}`
      : vaga.localizacao.cidade;
  const openVaga = () => {
    window.open(vaga.urlAplicacao, "_blank", "noopener,noreferrer");
  };
  const futureJobMatch = vaga.titulo.match(
    /^(Não encontrou a sua vaga em )(.+?)(\?.*)$/,
  );

  if (isVagaFutura) {
    return (
      <div
        role="link"
        tabIndex={0}
        onClick={openVaga}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openVaga();
          }
        }}
        className="group relative block cursor-pointer bg-white rounded-lg p-5 border border-transparent shadow-[0px_0px_2px_0px_rgba(0,0,0,0.1),0px_1px_2px_0px_rgba(0,0,0,0.07),0px_1px_3px_0px_rgba(0,0,0,0.06)] hover:shadow-md hover:border-blue-main transition-colors transition-shadow focus:outline-none focus:border-blue-main"
      >
        <div className="relative z-10 flex flex-col gap-4 md:flex-row md:items-center md:justify-between md:gap-6">
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <p className="text-base text-black/48 leading-normal">
                {vaga.departamento}
              </p>
              <span className="inline-flex items-center rounded-[4px] bg-[#F5E2A8] px-1 py-1 text-xs font-normal leading-none text-[#644D12]">
                {tipoLabel}
              </span>
            </div>

            <h3 className="text-base text-text-primary leading-normal font-normal">
              {futureJobMatch ? (
                <>
                  {futureJobMatch[1]}
                  <strong className="font-semibold">{futureJobMatch[2]}</strong>
                  {futureJobMatch[3]}
                </>
              ) : (
                vaga.titulo
              )}
            </h3>

            <div className="mt-2 flex items-center gap-1.5">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                className="text-text-primary/72 shrink-0"
              >
                <path
                  d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z"
                  fill="#0064F5"
                />
              </svg>
              <span className="text-sm text-text-primary/72">
                {locationLabel}
              </span>
            </div>
          </div>

          <a
            href={vaga.urlAplicacao}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => event.stopPropagation()}
            className="relative z-20 inline-flex items-center justify-center self-start rounded-full border border-[#6EA1FF] px-3 py-1.5 text-[15px] leading-5 font-semibold text-blue-main transition-colors hover:bg-blue-main/5 md:self-center"
          >
            Registrar interesse
          </a>
        </div>
      </div>
    );
  }

  return (
    <a
      href={vaga.urlAplicacao}
      target="_blank"
      rel="noopener noreferrer"
      className="block bg-white rounded-lg p-5 border border-transparent shadow-[0px_0px_2px_0px_rgba(0,0,0,0.1),0px_1px_2px_0px_rgba(0,0,0,0.07),0px_1px_3px_0px_rgba(0,0,0,0.06)] hover:shadow-md hover:border-blue-main transition-colors transition-shadow"
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <p className="text-base text-black/48 leading-normal">
              {vaga.departamento}
            </p>
            <h3 className="text-base text-text-primary leading-normal font-normal">
              {vaga.titulo}
            </h3>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              className="text-text-primary/72"
            >
              <path
                d="M11.99 2C6.47 2 2 6.48 2 12C2 17.52 6.47 22 11.99 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 11.99 2ZM12 20C7.58 20 4 16.42 4 12C4 7.58 7.58 4 12 4C16.42 4 20 7.58 20 12C20 16.42 16.42 20 12 20Z"
                fill="currentColor"
              />
              <path
                d="M12.5 7H11V13L16.25 16.15L17 14.92L12.5 12.25V7Z"
                fill="currentColor"
              />
            </svg>
            <span className="text-sm text-text-primary/72 whitespace-nowrap">
              {formatTimeAgo(vaga.dataAtualizacao)}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            className="text-text-primary/72 shrink-0"
          >
            <path
              d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z"
              fill="#0064F5"
            />
          </svg>
          <span className="text-sm text-text-primary/72">{locationLabel}</span>
        </div>
      </div>
    </a>
  );
}
